import React, { type MutableRefObject } from 'react';
import { Checkbox, Form, Input } from 'antd';
import type { Rule } from 'antd/es/form';
import { useIntl } from 'react-intl';

import type { Stripe } from '@stripe/stripe-js';

import { BillingAddressFields } from '../billing-address-fields';
import { FormField } from '../form-field';
import { InstalmentCardFutureHeader } from '../instalment-card-future-header';
import { InstalmentDetailsIntro } from '../instalment-details-intro';
import { InstalmentPlanBlock } from '../instalment-plan-block';
import { IsolatedStripeElements } from '../isolated-stripe-elements';
import { SplitInstalmentDownChargeField } from '../split-instalment-down-charge-field';
import { StripeCardFields } from '../stripe-card-fields';
import type { StripeCardElementsGroup } from '../stripe-card-fields';
import type { InstalmentSummary } from '../../utils/payment-math';
import { usePaymentFormValidationRules } from '../../payment-form-validation-context';

import formStyles from '../../styles.module.scss';

interface InstalmentPaymentFlowSectionProps {
  downPaymentRules: Rule[];
  instalmentSummary: InstalmentSummary | null;
  isSubmitting: boolean;
  showSplitInstalmentDown: boolean;
  downForSplitRules: number;
  splitDownRules1: Rule[];
  splitDownRules2: Rule[];
  splitDownLastRef: MutableRefObject<'1' | '2'>;
  stripePromise: PromiseLike<Stripe | null> | null;
  billingAddressSameAsFirst: boolean | undefined;
  onOpenPaymentSchedule: () => void;
  setCard1Group: (group: StripeCardElementsGroup | null) => void;
  setCard2Group: (group: StripeCardElementsGroup | null) => void;
}

export function InstalmentPaymentFlowSection({
  downPaymentRules,
  instalmentSummary,
  isSubmitting,
  showSplitInstalmentDown,
  downForSplitRules,
  splitDownRules1,
  splitDownRules2,
  splitDownLastRef,
  stripePromise,
  billingAddressSameAsFirst,
  onOpenPaymentSchedule,
  setCard1Group,
  setCard2Group,
}: InstalmentPaymentFlowSectionProps): React.ReactElement {
  const intl = useIntl();
  const validationRules = usePaymentFormValidationRules();
  const namePlaceholder = intl.formatMessage({ id: 'certificates.payment.nameOnCardPlaceholder' });
  const card1Title = `${intl.formatMessage({ id: 'certificates.payment.card1Section' })}:`;
  const card2Title = `${intl.formatMessage({ id: 'certificates.payment.card2Section' })}:`;

  return (
    <section className={formStyles.instalmentIntroSection}>
      <div className={formStyles.instalmentFlowSurface}>
        <InstalmentDetailsIntro />
        <InstalmentPlanBlock
          downPaymentRules={downPaymentRules}
          instalmentSummary={instalmentSummary}
          isSubmitting={isSubmitting}
          formItemClassName={formStyles.formItem}
          onOpenPaymentSchedule={onOpenPaymentSchedule}
        />
        {showSplitInstalmentDown && (
          <div className={formStyles.instalmentSplitRoot}>
            <Form.Item
              name="primaryInstalmentCard"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'certificates.payment.primaryCardRequired' }),
                },
              ]}
              className={formStyles.hiddenPrimaryInstalmentField}
            >
              <Input readOnly tabIndex={-1} autoComplete="off" />
            </Form.Item>
            <p className={formStyles.subsectionLabel}>
              {intl.formatMessage({ id: 'certificates.payment.firstPaymentSection' })}
            </p>
            <SplitInstalmentDownChargeField
              downTotal={downForSplitRules}
              fieldName="splitDownAmount1"
              editedKey="1"
              rules={splitDownRules1}
              formItemClassName={formStyles.formItem}
              splitDownLastRef={splitDownLastRef}
            />
            <InstalmentCardFutureHeader cardSlot="card1" sectionTitle={card1Title} />
            <FormField
              name="nameOnCard"
              label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
              variant="input"
              rules={validationRules.nameOnCard}
              className={formStyles.formItem}
            >
              <Input placeholder={namePlaceholder} />
            </FormField>
            <StripeCardFields isLoading={isSubmitting} onElementsReady={setCard1Group} />
            <BillingAddressFields variant="billing1" formItemClassName={formStyles.formItem} />

            <div className={formStyles.splitSecondPaymentBlock}>
              <p className={formStyles.subsectionLabel}>
                {intl.formatMessage({ id: 'certificates.payment.secondPaymentSection' })}
              </p>
              <SplitInstalmentDownChargeField
                downTotal={downForSplitRules}
                fieldName="splitDownAmount2"
                editedKey="2"
                rules={splitDownRules2}
                formItemClassName={formStyles.formItem}
                splitDownLastRef={splitDownLastRef}
              />
              <InstalmentCardFutureHeader cardSlot="card2" sectionTitle={card2Title} />
              <FormField
                name="nameOnCard2"
                label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
                variant="input"
                rules={validationRules.nameOnCard}
                className={formStyles.formItem}
              >
                <Input placeholder={namePlaceholder} />
              </FormField>
              {stripePromise ? (
                <IsolatedStripeElements stripe={stripePromise}>
                  <StripeCardFields
                    fieldSuffix="2"
                    isLoading={isSubmitting}
                    onElementsReady={setCard2Group}
                  />
                </IsolatedStripeElements>
              ) : null}
              <Form.Item
                name="billingAddressSameAsFirst"
                valuePropName="checked"
                className={formStyles.sameAsFirstCheckboxItem}
              >
                <Checkbox>
                  {intl.formatMessage({ id: 'certificates.payment.sameAsBilling1' })}
                </Checkbox>
              </Form.Item>
              {!billingAddressSameAsFirst && (
                <BillingAddressFields variant="billing2" formItemClassName={formStyles.formItem} />
              )}
            </div>
          </div>
        )}
        {!showSplitInstalmentDown && (
          <div className={formStyles.instalmentCardBillingBlock}>
            <p className={formStyles.subsectionLabel}>
              {intl.formatMessage({ id: 'certificates.payment.cardLabel' })}:
            </p>
            <FormField
              name="nameOnCard"
              label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
              variant="input"
              rules={validationRules.nameOnCard}
              className={formStyles.formItem}
            >
              <Input placeholder={namePlaceholder} />
            </FormField>
            <StripeCardFields isLoading={isSubmitting} onElementsReady={setCard1Group} />
            <BillingAddressFields variant="default" formItemClassName={formStyles.formItem} />
          </div>
        )}
      </div>
    </section>
  );
}
